import { useState, useEffect, useRef } from "react";
import EventDescriptionCards from "./EventDescriptionCards";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import RegistrationsClosedNotice from "@/components/feature/RegistrationsClosedNotice";
import UpcomingEventsWaitingState from "./UpcomingEventsWaitingState";

interface UpcomingEvent {
  id: number;
  title: string;
  description: string;
  image_url: string;
  event_datetime: string;
  location: string;
  max_spots: number;
  active: boolean;
  registrations_closed: boolean;
  unique_link_slug: string;
}

function getCountdown(dateStr: string): { days: number; hours: number; minutes: number; seconds: number; isPast: boolean } {
  const target = new Date(dateStr).getTime();
  const now = Date.now();
  const diff = target - now;

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
  }

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
    isPast: false,
  };
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "America/New_York",
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/New_York",
  });
}

function countLabel(value: number, singular: string, plural: string): string {
  return value === 1 ? singular : plural;
}

const EVENT_TIME_ZONE = "America/New_York";

// Retorna o timestamp (ms) em que o evento deve sumir do site:
// às 4h da madrugada do dia seguinte ao evento, no fuso America/New_York.
function getHideTimestamp(iso: string): number {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: EVENT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const readNy = (ts: number) => {
    const parts = fmt.formatToParts(new Date(ts));
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
    return {
      y: get("year"),
      mo: get("month"),
      d: get("day"),
      h: get("hour") % 24,
      mi: get("minute"),
      s: get("second"),
    };
  };

  const start = readNy(new Date(iso).getTime());
  let target = Date.UTC(start.y, start.mo - 1, start.d + 1, 4, 0, 0);
  for (let i = 0; i < 2; i++) {
    const back = readNy(target);
    const zoned = Date.UTC(back.y, back.mo - 1, back.d, back.h, back.mi, back.s);
    target += Date.UTC(start.y, start.mo - 1, start.d + 1, 4, 0, 0) - zoned;
  }
  return target;
}

export default function UpcomingEventsSection() {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [registrationCounts, setRegistrationCounts] = useState<Record<number, number>>({});
  const [hideTimestamps, setHideTimestamps] = useState<Record<number, number>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    async function loadEvents() {
      setLoading(true);
      setFetchError(null);

      try {
        const { data, error } = await supabase
          .from("upcoming_events")
          .select("id, title, description, image_url, event_datetime, location, max_spots, active, registrations_closed, unique_link_slug")
          .eq("active", true)
          .order("event_datetime", { ascending: true });

        if (error) {
          setFetchError(error.message);
        } else if (data) {
          const loaded = data as UpcomingEvent[];
          setEvents(loaded);

          const tsMap: Record<number, number> = {};
          loaded.forEach((e) => {
            tsMap[e.id] = getHideTimestamp(e.event_datetime);
          });
          setHideTimestamps(tsMap);

          // Fetch approved registration counts for all events
          if (data.length > 0) {
            const counts: Record<number, number> = {};
            await Promise.all(
              data.map(async (e) => {
                const { data: approvedCount, error: regError } = await supabase.rpc(
                  "event_approved_count",
                  { p_event_id: e.id }
                );
                if (!regError && typeof approvedCount === "number") {
                  counts[e.id] = approvedCount;
                }
              })
            );
            setRegistrationCounts(counts);
          }
        }
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : "Erro ao carregar eventos");
      } finally {
        setLoading(false);
      }
    }

    loadEvents();
  }, []);

  // Update countdown every second
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const visibleEvents = events.filter((e) => (hideTimestamps[e.id] ?? 0) > now);

  // Mantém o índice dentro dos limites caso a lista mude
  useEffect(() => {
    if (visibleEvents.length === 0) return;
    if (currentIndex >= visibleEvents.length) {
      setCurrentIndex(visibleEvents.length - 1);
    }
  }, [visibleEvents.length, currentIndex]);

  // Autoplay: avança automaticamente a cada 5s quando há mais de um evento.
  // Pausa enquanto o usuário estiver interagindo (hover no card ou ao tocar nas setas/dots).
  useEffect(() => {
    if (visibleEvents.length <= 1 || isPaused) return;
    const timer = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % visibleEvents.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [visibleEvents.length, isPaused]);

  const goPrev = () => {
    setCurrentIndex((i) => (i - 1 + visibleEvents.length) % visibleEvents.length);
  };
  const goNext = () => {
    setCurrentIndex((i) => (i + 1) % visibleEvents.length);
  };

  // Suporte a swipe (arrastar com o dedo) no mobile
  const touchStartX = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const threshold = 50;
    if (deltaX < -threshold) {
      goNext();
    } else if (deltaX > threshold) {
      goPrev();
    }
    touchStartX.current = null;
  };

  if (loading) {
    return (
      <section className="relative py-16 md:py-24 bg-white overflow-hidden">
        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
              Fique por dentro
            </p>
            <h2 className="font-display text-2xl md:text-4xl text-brown">
              Próximos Eventos
            </h2>
          </div>
          <div className="text-center py-12">
            <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" />
          </div>
        </div>
      </section>
    );
  }

  if (fetchError) {
    return (
      <section className="relative py-16 md:py-24 bg-white overflow-hidden">
        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
              Fique por dentro
            </p>
            <h2 className="font-display text-2xl md:text-4xl text-brown">
              Próximos Eventos
            </h2>
          </div>
          <div className="text-center py-12">
            <p className="font-body text-sm text-brown/60">{fetchError}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 inline-block px-6 py-2 rounded-full bg-primary text-white font-body text-sm tracking-wide hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (visibleEvents.length === 0) {
    return (
      <section className="relative py-16 md:py-24 bg-white overflow-hidden">
        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
              Fique por dentro
            </p>
            <h2 className="font-display text-2xl md:text-4xl text-brown">
              Próximos Eventos
            </h2>
          </div>
          <UpcomingEventsWaitingState />
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-16 md:py-24 bg-white overflow-hidden">
      {/* Main content */}
      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
            Fique por dentro
          </p>
          <h2 className="font-display text-2xl md:text-4xl text-brown">
            Próximos Eventos
          </h2>
          <p className="font-body text-sm md:text-base text-brown/60 mt-4 max-w-xl mx-auto">
            Encontros pensados com carinho para fortalecer nossa rede, trocar experiências e criar laços que transformam.
          </p>
        </div>

        <div className="relative" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
          {/* Track */}
          <div
            className="overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {visibleEvents.map((event) => {
                const countdown = getCountdown(event.event_datetime);
                const registeredCount = registrationCounts[event.id] || 0;
                const isSoldOut = event.max_spots > 0 && registeredCount >= event.max_spots;

                return (
                  <div key={event.id} className="w-full shrink-0">
                    <div className="max-w-3xl mx-auto">
                      <div className="group relative flex flex-col bg-gradient-to-br from-beige via-beige/90 to-nude/30 rounded-2xl transition-all duration-500 border border-nude/40 md:overflow-hidden">
                        {/* Image banner — full width on top, with strong glow frame */}
                        <div className="relative w-full shrink-0">
                          {/* Glow layer behind the image */}
                          <div className="absolute -inset-1 rounded-t-2xl glow-pulse-strong pointer-events-none z-0" />

                          {/* Visible frame border */}
                          <div className="absolute inset-0 rounded-t-2xl border-2 border-primary/20 pointer-events-none z-[2]" />

                          {/* Image wrapper */}
                          <div className="relative z-[1] w-full overflow-hidden rounded-t-2xl bg-nude/30">
                            {/* arte inteira, sem corte: o flyer tem rostos e textos embaixo */}
                            <img
                              src={event.image_url}
                              alt={event.title}
                              className="w-full h-auto block transition-transform duration-700 group-hover:scale-105"
                            />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-5 md:p-6 lg:p-7 flex flex-col">
                          {/* Countdown badge — in content column */}
                          {!countdown.isPast && (
                            <div className="inline-flex items-center gap-x-1.5 bg-primary/8 border border-primary/15 rounded-xl px-3 sm:px-4 py-2.5 mb-4 max-w-full">
                              <div className="flex items-baseline gap-1 shrink-0">
                                <span className="font-display text-base md:text-lg text-primary tabular-nums leading-none whitespace-nowrap">
                                  {String(countdown.days).padStart(2, "0")}
                                </span>
                                <span className="font-body text-[10px] md:text-[11px] text-primary/60 lowercase leading-none whitespace-nowrap">
                                  {countLabel(countdown.days, "dia", "dias")}
                                </span>
                              </div>
                              <span className="text-primary/20 text-sm font-light mx-0.5 shrink-0">:</span>
                              <div className="flex items-baseline gap-1 shrink-0">
                                <span className="font-display text-base md:text-lg text-primary tabular-nums leading-none whitespace-nowrap">
                                  {String(countdown.hours).padStart(2, "0")}
                                </span>
                                <span className="font-body text-[10px] md:text-[11px] text-primary/60 lowercase leading-none whitespace-nowrap">
                                  {countLabel(countdown.hours, "hora", "horas")}
                                </span>
                              </div>
                              <span className="text-primary/20 text-sm font-light mx-0.5 shrink-0">:</span>
                              <div className="flex items-baseline gap-1 shrink-0">
                                <span className="font-display text-base md:text-lg text-primary tabular-nums leading-none whitespace-nowrap">
                                  {String(countdown.minutes).padStart(2, "0")}
                                </span>
                                <span className="font-body text-[10px] md:text-[11px] text-primary/60 lowercase leading-none whitespace-nowrap">
                                  {countLabel(countdown.minutes, "minuto", "minutos")}
                                </span>
                              </div>
                              <span className="text-primary/20 text-sm font-light mx-0.5 shrink-0">:</span>
                              <div className="flex items-baseline gap-1 shrink-0">
                                <span className="font-display text-base md:text-lg text-primary tabular-nums leading-none whitespace-nowrap">
                                  {String(countdown.seconds).padStart(2, "0")}
                                </span>
                                <span className="font-body text-[10px] md:text-[11px] text-primary/60 lowercase leading-none whitespace-nowrap">
                                  {countLabel(countdown.seconds, "segundo", "segundos")}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Event title */}
                          <h3 className="font-display text-lg md:text-xl text-brown mb-3 leading-snug">
                            {event.title}
                          </h3>

                          {/* Meta info */}
                          <div className="flex flex-col gap-2 mb-4">
                            <div className="flex items-center gap-2 text-brown/70">
                              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                <i className="ri-calendar-line text-primary text-sm" />
                              </div>
                              <span className="font-body text-sm">
                                {formatDate(event.event_datetime)} às {formatTime(event.event_datetime)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-brown/70">
                              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                <i className="ri-map-pin-line text-primary text-sm" />
                              </div>
                              <span className="font-body text-sm">{event.location}</span>
                            </div>
                            {event.max_spots > 0 && (
                              <div className="flex items-center gap-2 text-brown/70">
                                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                  <i className="ri-user-line text-primary text-sm" />
                                </div>
                                <span className="font-body text-sm">
                                  {event.max_spots} vagas
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Description */}
                          {/* texto em cartões de deslizar (modelo do carrossel do Instagram) */}
                          <EventDescriptionCards title={event.title} description={event.description} imageUrl={event.image_url} />

                          {/* CTA — Closed / sold out / normal */}
                          {event.registrations_closed ? (
                            <RegistrationsClosedNotice className="mt-auto" />
                          ) : isSoldOut ? (
                            <div className="mt-auto space-y-3">
                              {/* Bold sold-out badge */}
                              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/20">
                                <div className="relative shrink-0 mt-0.5">
                                  <div className="absolute -inset-0.5 rounded-full bg-primary/20 blur-sm" />
                                  <div className="relative w-8 h-8 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                                    <i className="ri-vip-crown-line text-base text-primary" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-primary text-white font-body text-[10px] font-bold tracking-wider uppercase mb-1.5">
                                    <div className="w-3 h-3 flex items-center justify-center">
                                      <i className="ri-sparkling-line text-[9px]" />
                                    </div>
                                    Vagas preenchidas
                                  </div>
                                  <p className="font-body text-[13px] text-brown/70 leading-snug">
                                    Entre na <strong>lista de espera</strong>: se uma vaga abrir, chamamos pela ordem de chegada.
                                  </p>
                                </div>
                              </div>
                              <Link
                                to={`/proximo-evento/${event.unique_link_slug}`}
                                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap cursor-pointer"
                              >
                                Quero participar
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i className="ri-arrow-right-line text-sm" />
                                </div>
                              </Link>
                            </div>
                          ) : (
                            <Link
                              to={`/proximo-evento/${event.unique_link_slug}`}
                              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap cursor-pointer"
                            >
                              Quero participar
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i className="ri-arrow-right-line text-sm" />
                              </div>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation arrows */}
          {visibleEvents.length > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Evento anterior"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 rounded-full bg-white border border-nude text-brown flex items-center justify-center transition-all duration-300 hover:bg-primary hover:text-white hover:border-primary cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Próximo evento"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 rounded-full bg-white border border-nude text-brown flex items-center justify-center transition-all duration-300 hover:bg-primary hover:text-white hover:border-primary cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
              </button>
            </>
          )}
        </div>

        {/* Dots */}
        {visibleEvents.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            {visibleEvents.map((event, i) => (
              <button
                key={event.id}
                type="button"
                onClick={() => setCurrentIndex(i)}
                aria-label={`Ir para evento ${i + 1}`}
                className={
                  i === currentIndex
                    ? "w-8 h-2.5 rounded-full bg-primary transition-all duration-300 cursor-pointer"
                    : "w-2.5 h-2.5 rounded-full bg-nude hover:bg-primary/40 transition-all duration-300 cursor-pointer"
                }
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}